import React, { useRef, useState } from 'react';
import ImpressionsHandler from '@nykaa/deals-utils/impressions';
import { IMPRESSION_TYPE } from '@nykaa/deals-utils/impressions/constants';
import ClickHandler from '@nykaa/deals-utils/clicks';
import { appLinkTypes, webActionTypes } from '@nykaa/deals-utils/clicks/constants';
import constants from '@nykaa/deals-utils/basic/constants';
import { PRODUCT_EVENT } from '../../constants';
import CONFIGURATIONS, { buttonTexts } from './constants';
import { handleRightClickCallback } from '../../helper';
const useProductWidget = ({ params, widgetData, store, }) => {
    const [isLoading, setIsLoading] = useState(false);
    const childPositionRef = useRef(null);
    const { trackingData: oldTrackingData, isDevModeEnabled, devModeData, childPosition, isDesktop, cta, } = widgetData;
    const { id, slug, highlight, sku, price, mrp, insideTag, quantity, title, brandName, parentWidgetId, appLinkData, appLinkType, trackingParams, variantType, outsideTag, inStock, tagText, name2, } = params;
    const { FASHION_FOOTLOCKER_STORE } = constants;
    React.useEffect(() => {
        childPositionRef.current = childPosition;
    }, [childPosition]);
    const width = 1000;
    // Dev Mode
    const devModeHandler = !!isDevModeEnabled && {
        onContextMenu: () => handleRightClickCallback(devModeData),
    };
    // Card Height and Width
    const definedWidth = width * CONFIGURATIONS.CARD_WIDTH_RATIO;
    const definedHeight = width * CONFIGURATIONS.CARD_HEIGHT_RATIO;
    let cardWidth = width > CONFIGURATIONS.MAX_DEVICE_WIDTH
        ? CONFIGURATIONS.MIN_CARD_WIDTH
        : definedWidth;
    cardWidth = isDesktop ? CONFIGURATIONS.MAX_CARD_WIDTH : cardWidth;
    let cardHeight = width > CONFIGURATIONS.MAX_DEVICE_WIDTH
        ? CONFIGURATIONS.MIN_CARD_HEIGHT
        : definedHeight;
    cardHeight = isDesktop ? CONFIGURATIONS.MAX_CARD_HEIGHT : cardHeight;
    let formattedSlug = slug || '';
    if (formattedSlug && formattedSlug[0] !== '/') {
        formattedSlug = `/${formattedSlug}`;
    }
    const isHighlight = !!(highlight && highlight.length > 0);
    const { handleAddToCart, wishlist } = cta;
    const { wishlistData, handleWishList } = wishlist;
    const isProductInWishlist = React.useMemo(() => wishlistData && wishlistData.length > 0 && wishlistData.indexOf(id.toString()) !== -1, [wishlistData, id]);
    const dnImpressionData = {
        position1: oldTrackingData.position1,
        dn_page_type: oldTrackingData.namespace0,
        dn_page_section: oldTrackingData.namespace1,
        dn_tile_id: oldTrackingData.namespace2,
        dn_inventory_position: oldTrackingData.position1 - 1,
        dn_tile_position: childPosition,
        dn_transaction_id: oldTrackingData.transaction_id || `wid_${parentWidgetId}`,
        dn_tracking_metadata: params.trackingData.dn_tracking_metadata,
        dn_request_page_data: oldTrackingData.pageData1,
        dn_inventory_page_data: oldTrackingData.pageData2,
        dn_wtype: oldTrackingData.wtype,
        dn_wtype_version: oldTrackingData.wtype_version,
    };
    const isFashionFootLockerStore = !!(FASHION_FOOTLOCKER_STORE.includes(store));
    const productTrackingData = {
        product_sku: sku,
        product_price: price,
        product_mrp: mrp,
        product_dp: price,
        product_inventory: quantity,
        product_name: isFashionFootLockerStore ? name2 : title,
        product_tags: [tagText || outsideTag] || [],
        product_badges: [insideTag] || [],
        product_id: id,
        product_position: childPosition,
        product_brand: brandName,
        page_url: typeof window !== 'undefined' ? window.location.href : '',
    };
    const preparedProductTrackingData = Object.assign(Object.assign({}, productTrackingData), dnImpressionData);
    const clickedTrackingData = Object.assign(Object.assign({}, preparedProductTrackingData), { event_name: PRODUCT_EVENT.CLICK, event_type: 'click' });
    const handleIntersect = () => {
        ImpressionsHandler.syncCallback([Object.assign(Object.assign({}, preparedProductTrackingData), { hit_recorded_at: new Date().getTime() / 1000, event: 'product:impressions', event_name: PRODUCT_EVENT.IMPRESSIONS, dn_tile_position: childPositionRef === null || childPositionRef === void 0 ? void 0 : childPositionRef.current, product_position: childPositionRef === null || childPositionRef === void 0 ? void 0 : childPositionRef.current, id })], IMPRESSION_TYPE.PRODUCT);
    };
    const clickHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        ImpressionsHandler.handleClick(Object.assign(Object.assign({}, clickedTrackingData), { dn_tile_position: childPositionRef === null || childPositionRef === void 0 ? void 0 : childPositionRef.current, product_position: childPositionRef === null || childPositionRef === void 0 ? void 0 : childPositionRef.current })).finally(() => {
            /**
             * ? Why finally?
             * even in case of an error in handling impression...
             * we would want the click to function as expected
             */
            ClickHandler.handleClick({
                appLinkData,
                appLinkType,
                trackingParams,
                webActionType: webActionTypes.URL_REDIRECTION,
                webActionUrl: formattedSlug,
                isDesktop,
            });
        });
    };
    const handleWishListClick = () => {
        handleWishList(params, isProductInWishlist, Object.assign({}, preparedProductTrackingData));
    };
    const handleAddToCartClick = () => {
        if (!isLoading) {
            handleAddToCart(params, setIsLoading, Object.assign({}, preparedProductTrackingData));
            const isShade = (variantType === 'Shade');
            const isSize = (variantType === 'Size');
            const isNative = () => {
                if (isShade) {
                    return appLinkTypes.CTA_CALLBACKS_SHADE;
                }
                if (isSize) {
                    return appLinkTypes.CTA_CALLBACKS_SIZE;
                }
                return null;
            };
            ClickHandler.handleClick({
                appLinkData: id,
                appLinkType: isNative(),
                trackingParams: null,
                webActionType: 'none',
                webActionUrl: '',
                isDesktop,
            });
        }
    };
    const getCTAtext = () => {
        if (!inStock) {
            return buttonTexts.NOTIFY_ME;
        }
        if (isLoading) {
            return buttonTexts.ADDING;
        }
        const varintTypeButtonText = `${CONFIGURATIONS.SELECT_TEXT} ${variantType}`;
        return variantType ? varintTypeButtonText : buttonTexts.ADD_TO_CART;
    };
    return {
        devModeHandler,
        cardWidth,
        cardHeight,
        isHighlight,
        handleWishListClick,
        isProductInWishlist,
        clickHandler,
        handleIntersect,
        getCTAtext,
        handleAddToCartClick,
    };
};
export default useProductWidget;
